import { useCallback, useEffect, useRef } from 'react'
import {
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import type { JwtTokenFn } from '@me8eon/authentication'
import { entraApiScope } from '../config/auth.config'
import type { AuthenticatedFetch } from '../types/network'

function selectedAccount(
  activeAccount: AccountInfo | null,
  accounts: AccountInfo[],
): AccountInfo | null {
  return activeAccount ?? accounts[0] ?? null
}

function useStableAccount() {
  const { accounts, instance } = useMsal()
  const account = selectedAccount(instance.getActiveAccount(), accounts)

  // getActiveAccount() rebuilds an AccountInfo from the cache on every call, so `account`
  // is a new object each render. Key the callback on the account's stable id instead and
  // read the object from a ref, otherwise every consumer's effects re-fire per render.
  const accountRef = useRef(account)
  const accountId = account?.homeAccountId ?? null

  useEffect(() => {
    accountRef.current = account
  })

  useEffect(() => {
    if (account && !instance.getActiveAccount()) {
      instance.setActiveAccount(account)
    }
  }, [account, instance])

  return { accountRef, instance, accountId }
}

export function useAuthenticatedFetch(): AuthenticatedFetch {
  const { accountRef, instance, accountId } = useStableAccount()

  return useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const account = accountRef.current
      if (!account) {
        throw new Error('An authenticated Microsoft Entra account is required')
      }

      let accessToken: string

      try {
        const response = await instance.acquireTokenSilent({
          account,
          scopes: [entraApiScope],
        })
        accessToken = response.accessToken
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({
            account,
            scopes: [entraApiScope],
          })
        }
        throw error
      }

      const headers = new Headers(init?.headers)
      headers.set('Authorization', `Bearer ${accessToken}`)

      return fetch(input, {
        ...init,
        headers,
      })
    },
    [accountId, instance],
  )
}

// Gives OCR/axios-style clients (whose config shape expects a bare
// `() => Promise<string | undefined>`) a way to reuse the same MSAL
// silent/redirect token flow as useAuthenticatedFetch, without needing a
// fetch wrapper. Resolves to undefined rather than throwing when there's no
// account yet, matching JwtTokenFn's contract — callers surface their own
// "no token" error.
export function useEntraTokenFn(): JwtTokenFn {
  const { accountRef, instance, accountId } = useStableAccount()

  return useCallback(async () => {
    const account = accountRef.current
    if (!account) {
      return undefined
    }

    try {
      const response = await instance.acquireTokenSilent({
        account,
        scopes: [entraApiScope],
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          account,
          scopes: [entraApiScope],
        })
      }
      throw error
    }
  }, [accountId, instance])
}
